import { useState } from 'react';
import { Folder, Plus, PanelLeftClose, PanelLeft, Bot } from 'lucide-react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ImportBrowserBookmarks } from './ImportBrowserBookmarks';
import { DroppableCollectionItem } from './DroppableCollectionItem';
import { AISettingsDialog } from './AISettingsDialog';
import { TourMenu } from './TourMenu';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { toast } from 'sonner';
import { collectionsApi } from '../lib/api';
import { collectionIcons, getIconByName } from '../lib/icons';
import { isAIConfigured } from '../lib/ai';
import type { CollectionWithCount } from '../types/database';

interface SidebarProps {
  collections: CollectionWithCount[];
  selectedCollectionId: string | null;
  onSelectCollection: (id: string | null) => void;
  onCollectionsChange: () => void;
  onBookmarksChange?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function Sidebar({
  collections,
  selectedCollectionId,
  onSelectCollection,
  onCollectionsChange,
  onBookmarksChange,
  collapsed = false,
  onToggleCollapse,
}: SidebarProps) {
  const [open, setOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState<CollectionWithCount | null>(null);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('folder');
  const [color, setColor] = useState('#0F766E');

  const handleOpenCreate = () => {
    setEditingCollection(null);
    setName('');
    setDescription('');
    setIcon('folder');
    setColor('#0F766E');
    setOpen(true);
  };

  const handleOpenEdit = (collection: CollectionWithCount) => {
    setEditingCollection(collection);
    setName(collection.name);
    setDescription(collection.description || '');
    setIcon(collection.icon || 'folder');
    setColor(collection.color || '#0F766E');
    setOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('이름을 입력해주세요');
      return;
    }

    setLoading(true);
    try {
      if (editingCollection) {
        // Update existing collection
        await collectionsApi.update(editingCollection.id, {
          name,
          description: description || null,
          icon: icon || null,
          color: color || null,
        });
        toast.success('수정했어요');
      } else {
        // Create new collection
        await collectionsApi.create({
          name,
          description: description || null,
          icon: icon || null,
          color: color || null,
        });
        toast.success('만들었어요');
      }

      setOpen(false);
      onCollectionsChange();
    } catch (error: any) {
      console.error('Error saving collection:', error);
      toast.error('저장에 문제가 생겼어요');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('삭제할까요?')) return;

    try {
      await collectionsApi.delete(id);
      toast.success('삭제했어요');
      if (selectedCollectionId === id) {
        onSelectCollection(null);
      }
      onCollectionsChange();
    } catch (error: any) {
      console.error('Error deleting collection:', error);
      toast.error('삭제에 문제가 생겼어요');
    }
  };

  // Droppable for "All bookmarks" - to remove bookmark from collection
  const { isOver: isOverAll, setNodeRef: setAllRef } = useDroppable({
    id: 'all-bookmarks',
  });

  const colors = [
    { value: '#0F766E', label: '초록색' },
    { value: '#3b82f6', label: '파란색' },
    { value: '#8b5cf6', label: '보라색' },
    { value: '#f59e0b', label: '주황색' },
    { value: '#ef4444', label: '빨간색' },
    { value: '#ec4899', label: '분홍색' },
    { value: '#06b6d4', label: '청록색' },
    { value: '#737373', label: '회색' },
  ];

  // Collection Dialog (shared between collapsed and expanded views)
  const CollectionDialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {editingCollection ? '컬렉션 수정' : '새 컬렉션'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 pt-4">
          <div className="space-y-2">
            <Label htmlFor="icon">아이콘</Label>
            <div className="grid grid-cols-5 gap-1.5">
              {collectionIcons.map((iconOption) => {
                const IconComponent = iconOption.icon;
                return (
                  <button
                    key={iconOption.name}
                    type="button"
                    onClick={() => setIcon(iconOption.name)}
                    className={`p-2.5 rounded-md border-2 transition-all hover:bg-accent ${
                      icon === iconOption.name
                        ? 'border-primary bg-accent'
                        : 'border-border'
                    }`}
                    title={iconOption.label}
                  >
                    <IconComponent className="w-4 h-4 mx-auto" />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="color">색상</Label>
            <div className="grid grid-cols-8 gap-1.5">
              {colors.map((colorOption) => (
                <button
                  key={colorOption.value}
                  type="button"
                  onClick={() => setColor(colorOption.value)}
                  className={`w-8 h-8 rounded-md border-2 transition-all ${
                    color === colorOption.value
                      ? 'border-foreground scale-110'
                      : 'border-border'
                  }`}
                  style={{ backgroundColor: colorOption.value }}
                  title={colorOption.label}
                />
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">이름 *</Label>
            <Input
              id="name"
              type="text"
              placeholder="컬렉션 이름"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">설명</Label>
            <Textarea
              id="description"
              placeholder="컬렉션 설명 (선택사항)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              취소
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? '저장 중...' : '저장'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <TooltipProvider delayDuration={300}>
      <aside
        data-tour="sidebar"
        className={`${collapsed ? 'w-14' : 'w-60'} border-r border-border bg-sidebar flex flex-col transition-all duration-200`}
      >
        {/* Header */}
        <div className={`${collapsed ? 'px-2' : 'px-3'} py-3 border-b border-sidebar-border`}>
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'} mb-3`}>
            {!collapsed && <h2 className="text-sm font-semibold text-sidebar-foreground">컬렉션</h2>}
            <div className="flex items-center gap-1">
              {!collapsed && (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="새 컬렉션 만들기"
                  data-tour="add-collection"
                  onClick={handleOpenCreate}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onToggleCollapse}
                    aria-label={collapsed ? '사이드바 펼치기' : '사이드바 접기'}
                  >
                    {collapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {collapsed ? '사이드바 펼치기' : '사이드바 접기'}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          {/* All bookmarks button */}
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  ref={setAllRef}
                  onClick={() => onSelectCollection(null)}
                  className={`w-full flex items-center justify-center p-2 rounded-md text-sm transition-all ${
                    selectedCollectionId === null
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                      : 'text-sidebar-foreground hover:bg-sidebar-accent/50'
                  } ${isOverAll ? 'ring-2 ring-primary bg-primary/10' : ''}`}
                  aria-label="모든 북마크"
                >
                  <Folder className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">모든 북마크</TooltipContent>
            </Tooltip>
          ) : (
            <button
              ref={setAllRef}
              onClick={() => onSelectCollection(null)}
              className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-sm transition-all ${
                selectedCollectionId === null
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                  : 'text-sidebar-foreground hover:bg-sidebar-accent/50'
              } ${isOverAll ? 'ring-2 ring-primary bg-primary/10' : ''}`}
            >
              <Folder className="w-4 h-4" />
              <span className="flex-1 text-left">모든 북마크</span>
            </button>
          )}
        </div>

        {/* Collections nav */}
        <nav
          data-tour="collections-list"
          className={`flex-1 overflow-y-auto ${collapsed ? 'px-2' : 'px-3'} py-2`}
        >
          {collections.length === 0 ? (
            !collapsed && (
              <p className="text-xs text-muted-foreground text-center py-6">
                컬렉션이 없습니다
              </p>
            )
          ) : collapsed ? (
            // Collapsed view - icons only with tooltips
            <div className="space-y-1">
              {collections.map((collection) => {
                const IconComponent = getIconByName(collection.icon || 'folder');
                const iconColor = collection.color || '#6b7280';
                return (
                  <Tooltip key={collection.id}>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => onSelectCollection(collection.id)}
                        className={`w-full flex items-center justify-center p-2 rounded-md transition-all ${
                          selectedCollectionId === collection.id
                            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                            : 'text-sidebar-foreground hover:bg-sidebar-accent/50'
                        }`}
                        aria-label={collection.name}
                      >
                        <IconComponent className="w-4 h-4" style={{ color: iconColor }} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      {collection.name} ({collection.bookmark_count || 0})
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          ) : (
            // Expanded view - full collection items
            <SortableContext
              items={collections.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-0.5">
                {collections.map((collection) => (
                  <DroppableCollectionItem
                    key={collection.id}
                    collection={collection}
                    isSelected={selectedCollectionId === collection.id}
                    onSelect={() => onSelectCollection(collection.id)}
                    onEdit={() => handleOpenEdit(collection)}
                    onDelete={() => handleDelete(collection.id)}
                  />
                ))}
              </div>
            </SortableContext>
          )}
        </nav>

        {/* Import browser bookmarks & create button for collapsed */}
        <div className={`${collapsed ? 'px-2' : 'px-3'} py-3 border-t border-sidebar-border space-y-2`}>
          {collapsed ? (
            <div className="space-y-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={handleOpenCreate}
                    aria-label="새 컬렉션 만들기"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">새 컬렉션</TooltipContent>
              </Tooltip>
              <AISettingsDialog
                trigger={
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full relative"
                        aria-label="AI 설정"
                        data-tour="ai-settings"
                      >
                        <Bot className="w-4 h-4" />
                        {isAIConfigured() && (
                          <span className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">AI 설정</TooltipContent>
                  </Tooltip>
                }
              />
              <TourMenu collapsed />
            </div>
          ) : (
            <>
              <ImportBrowserBookmarks
                collections={collections}
                onImportComplete={() => {
                  onCollectionsChange();
                  onBookmarksChange?.();
                }}
              />
              <AISettingsDialog
                trigger={
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-muted-foreground hover:text-foreground"
                    data-tour="ai-settings"
                  >
                    <Bot className="w-4 h-4 mr-2" />
                    AI 설정
                    {isAIConfigured() && (
                      <span className="ml-auto w-2 h-2 bg-green-500 rounded-full" />
                    )}
                  </Button>
                }
              />
              <TourMenu />
            </>
          )}
        </div>

        {/* Shared Dialog */}
        {CollectionDialog}
      </aside>
    </TooltipProvider>
  );
}
